import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, finalize, switchMap, filter, take } from 'rxjs/operators';

import { AuthService } from 'src/app/auth/services/auth.service';
import { NotificationService } from 'src/app/utils/notification.service';
import { LoadingService } from '../services/loading.service';
import { SUPPRESS_ERROR_TOAST } from './http-context-tokens';

@Injectable({
  providedIn: 'root',
})
export class WizerHttpInterceptor implements HttpInterceptor {
  numberOfTimesErrorOccured: any = 0;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );

  constructor(
    private toast: NotificationService,
    private authService: AuthService,
    private _loading: LoadingService,
    private router: Router,
  ) {}

  private excludedEndpoints(req: HttpRequest<any>): boolean {
    const excludedEnpoints = [
      'login',
      'forgot-password',
      'verifyToken',
      // `users/reset-password` (first-time login password change) is authenticated with the token saved at login,
      // so it is deliberately not excluded here.
      'refresh_token',
      'invalidate-token',
    ];
    const endpointParts = req.url.split('/');
    const endpoint = endpointParts[endpointParts.length - 1];
    // exception for enpoint (['https:', '', 'geolocation-db.com', 'json', ''])
    if (endpointParts[endpointParts.length - 2] === 'json') return true;
    // skip auth headers for static assets (icons, images, etc.)
    if (req.url.includes('/assets/')) return true;
    return excludedEnpoints.includes(endpoint);
  }

  private excludedSingleEndpointsLoader(req: HttpRequest<any>): boolean {
    const excludedSingleEndpoint = ['messages'];
    const removedSingleQueryParams = req.url.split('?');
    const endpointSingleParts = removedSingleQueryParams[0].split('/');
    return excludedSingleEndpoint.includes(
      endpointSingleParts[endpointSingleParts.length - 1],
    );
  }

  private excludedEndpointsLoader(req: HttpRequest<any>): boolean {
    if (req.headers.get('showLoading') === 'false') {
      return true;
    }

    // Suppress global loader for in-app notification requests — the module has its own loader
    if (
      req.url.includes('in-app/notifications') ||
      req.url.includes('core/data/')
    ) {
      return true;
    }

    const excludedEnpoints = [
      'branch/list-branch',
      'areas/all-areas',
      'users/loan-officers',
      'savings-products/all',
      'loan-products/getAllLoanProducts',
      'depositAccounts/download',
      'reports/export-to-excel',
      'reports/export-to-pdf',
      'journal-entries/download',
      'journal-entries/compact-download',
      'reports/branchClientOverviewDownload',
      'reports/top-client-excel',
      'depositAccountReport/download',
      'api/refresh_token',
      'transactions/download',
      'clients/approvedClients',
      'loan-accounts/disbursedLoans',
      'loan-accounts/amountDisbursed',
      'loan-accounts/runningLoans',
      'loan-accounts/loansOutstanding',
      'loan-accounts/depositBalance',
      'loan-accounts/totalRepayment',
      'loan-accounts/loanProductPar',
      'tickets/search',
      'notifications/stats',
      'credit-bureau/download-excel',
    ];
    const removedQueryParams = req.url.split('?');
    const endpointParts = removedQueryParams[0].split('/');
    const endpoint = `${endpointParts[endpointParts.length - 2]}/${
      endpointParts[endpointParts.length - 1]
    }`;
    return excludedEnpoints.includes(endpoint);
  }

  private excludedRouteUrlsLoader(route: Router): boolean {
    const excludedRouteUrls = ['/'];
    const currentRoute = route.url;
    return excludedRouteUrls.includes(currentRoute);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    /* A request started while a screen holds a quiet scope (see
       LoadingService.beginQuietScope) runs without the global loader, and is
       counted so the scope can end once it has finished. */
    if (!this._loading.isQuiet) return this.handleRequest(req, next, false);
    this._loading.quietRequestStarted();
    return this.handleRequest(req, next, true).pipe(
      finalize(() => this._loading.quietRequestEnded()),
    );
  }

  private handleRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
    quiet: boolean,
  ) {
    /* exclude endpoint and route loader, and quiet requests */
    if (
      !quiet &&
      !this.excludedEndpointsLoader(req) &&
      !this.excludedRouteUrlsLoader(this.router)
    )
      this._loading.setLoading(true, req.url);

    /* exclude auth */
    if (this.excludedEndpoints(req))
      return next
        .handle(req)
        .pipe(finalize(() => this._loading.setLoading(false, req.url)));

    if (req.url.split('/')[req.url.split('/').length - 1] == 'keep-alive') {
    }

    const regularExpression =
      /accounting\/closure\/toggle-year-end-closure\?id=[^&]/;

    if (
      req.url.match(regularExpression) &&
      req.headers.get('showLoading') === 'false'
    ) {
      this.toast.info(
        $localize`:@@request_submitted_system_processing:Request Submitted System Processing...`,
      );
    }

    if (
      req.url.split('/')[req.url.split('/').length - 1] == 'export-to-excel'
    ) {
      this.toast.info($localize`:@@download_starting:Download Starting...`);
    }

    if (
      req.url.split('/')[req.url.split('/').length - 1] == 'download-excel' &&
      req.url.includes('credit-bureau')
    ) {
      this.toast.info($localize`:@@download_starting:Download Starting...`);
    }

    const token = localStorage['token']?.replaceAll('"', '');
    if (!token) this.router.navigate(['login']);
    const locale = localStorage.getItem('locale');
    const newReq = req.clone({
      // Cache-Control: no-cache is the one valid request-side cache-buster; the backends already send
      // no-store/no-cache RESPONSE headers anyway. Pragma (legacy), Expires (a response-only header), and
      // If-Modified-Since:'0' (malformed date) were removed: as request headers they add nothing and, being
      // non-simple headers, they forced the CORS preflight to demand Expires/Pragma be allow-listed at the
      // ingress — every authenticated call failed preflight without that.
      headers: req.headers
        .set('Authorization', `Bearer ${token}`)
        .set('Cache-Control', 'no-cache')
        .set('Accept-Language', locale),
    });

    return next.handle(newReq).pipe(
      catchError((err) => this.handleError(err, next, newReq)),
      finalize(() => {
        if (
          req.url.split('/')[req.url.split('/').length - 1] == 'export-to-excel'
        ) {
          this.toast.success(
            $localize`:@@download_completed:Download Completed`,
          );
        }
        if (
          req.url.split('/')[req.url.split('/').length - 1] ==
            'download-excel' &&
          req.url.includes('credit-bureau')
        ) {
          this.toast.success(
            $localize`:@@download_completed:Download Completed`,
          );
        }
        if (
          req.url.match(regularExpression) &&
          req.headers.get('showLoading') === 'false'
        ) {
          this.toast.success(
            $localize`:@@request_approved_and_completed:Request Approved & Completed`,
          );
        }
        this._loading.setLoading(false, newReq.url);
      }),
    );
  }

  handleResponse(req: HttpRequest<any>, data) {}

  handleError(
    err: HttpErrorResponse,
    next: HttpHandler,
    req: HttpRequest<any>,
  ) {
    // const that = this;
    // Staff Module and My Aquila surface request failures in their own UI (toast,
    // field error, or inline state). Letting the global interceptor toast as well
    // produces two identical notifications for the same response.
    const featureHandlesError = /\/(staff-module|my-aquila)(\/|$)/.test(req.url);

    // The caller reports this failure itself (on a card, panel, etc.). Applies
    // to every status below, not just 4xx — a 500 used to toast regardless.
    const suppressToast =
      req.context.get(SUPPRESS_ERROR_TOAST) ||
      !!req.headers.get('suppress-error-toast') ||
      featureHandlesError;

    if (err.status == 504 || err.status == 0) {
      // if (that.numberOfTimesErrorOccured == 1) {
      // this.toast.warning('Warning!. Please if you are unable to load up data on application, please log out and log back in or refresh the page. Sorry for the inconvenience');
      // }
    } else if (
      err.status == 503 ||
      err.error?.message === 'Unable to find instance for NEW-CORE-SERVICE'
    ) {
      if (!suppressToast) {
        this.toast.error(
          $localize`:@@server_not_reachable:Server not reachable!`,
        );
      }
    } else if (err.status == 500) {
      if (!suppressToast) {
        this.toast.error($localize`:@@an_error_occured:An Error Occured!`);
      }
    } else if (err.error?.code == 97) {
      //session timed out
      $('#modal-lock-screen').show();
    } else if (err.status == 401) {
      //expired token
      return this.refreshToken(req, next);
    } else {
      if (!suppressToast) {
        this.toast.error(err.error?.message || err.error?.error);
      }
    }

    return throwError(() => err);
  }

  private addToken(req: HttpRequest<any>, token: string) {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private refreshToken(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(res.data.access_token);
          return next.handle(this.addToken(request, res.data.access_token));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          if (err?.status === 403) {
            localStorage.clear();
            this.toast.error('Session Timeout');
            this.router.navigate(['login']);
          }
          return throwError(() => err);
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        }),
      );
    }
  }
}
